/**
 * useDecryptedContent — decrypts secret field content for display.
 *
 * Handles three states:
 *  1. Content is plaintext (not encrypted) → returns it as-is.
 *  2. Content is encrypted and noteKey is available → decrypts and returns plaintext.
 *  3. Content is encrypted but noteKey is NOT available → returns null (locked).
 */

import { useState, useEffect } from 'react';
import { isEncryptedFieldValue, decryptFieldValue } from '@/services/CryptoService';
import { useNoteKeyStore } from '@/store/noteKeyStore';
import { getCurrentNoteId } from './secretFieldContext';

export interface DecryptedContentResult {
    /** The plaintext to display/edit. Null when encrypted but key is unavailable. */
    plaintext: string | null;
    /** True when the content is encrypted (regardless of whether key is available). */
    isEncrypted: boolean;
    /** True when decryption failed (wrong key or tampered data). */
    decryptionFailed: boolean;
}

/**
 * Decrypt secret field content for display.
 *
 * @param content - Raw content from the SecretFieldNode (plaintext or `enc:…`).
 * @returns       - Decrypted content result.
 */
export function useDecryptedContent(content: string): DecryptedContentResult {
    const noteId = getCurrentNoteId();
    const noteKey = useNoteKeyStore((s) => noteId ? s.getNoteKey(noteId) : null);

    const [result, setResult] = useState<DecryptedContentResult>(() =>
        computeResult(content, noteKey),
    );

    useEffect(() => {
        setResult(computeResult(content, noteKey));
    }, [content, noteKey]);

    return result;
}

function computeResult(
    content: string,
    noteKey: Uint8Array | null,
): DecryptedContentResult {
    if (!isEncryptedFieldValue(content)) {
        // Plaintext — return as-is.
        return { plaintext: content, isEncrypted: false, decryptionFailed: false };
    }

    if (!noteKey) {
        // Encrypted but no key available — locked.
        return { plaintext: null, isEncrypted: true, decryptionFailed: false };
    }

    try {
        const plaintext = decryptFieldValue(content, noteKey);
        return { plaintext, isEncrypted: true, decryptionFailed: false };
    } catch {
        // Decryption failed — wrong key or tampered data.
        return { plaintext: null, isEncrypted: true, decryptionFailed: true };
    }
}

